/*
 * ─── เขียน prompt สำหรับ Google Flow (choices 27 ต.ค. 2569 07:14: ข "Gemini ในระบบคิดให้" · prompt เลือกภาษาไทย/อังกฤษได้ ·
 * เฉพาะคนที่มีสิทธิ์ "รูป AI" · เพดานเงินกองเดียวกับรูป AI · รอบ 1 = prompt ทีละช็อต, รอบ 2 = โครงงานวิดีโอ).
 * The person writes what they want in plain words; Gemini (text only — background mode takes text, see 26 ต.ค. probe)
 * turns it into one shared style block and a prompt for each shot, to paste into Google Flow (Veo). On-screen Thai text
 * and narration are kept apart (they go into CraftStudio, not into the generated video). No DOM, no Fabric: the Worker
 * and the app both use checkFlow() — the same checker on the server and in the app (BUG-13), on Gemini's raw answer (BUG-72).
 * What makes a good Veo prompt (third-party guide, akool.com "Google Veo 3.1 Video Prompt Guide", read 27 ต.ค.: Google's
 * own page did not load): style, subject, action, setting and light, camera, sound, aspect ratio (default 16:9),
 * whole sentences, a negative prompt such as "no text, no subtitles"; Ingredients (up to 3 reference images) or Frames
 * (first and last frame) keep shots alike.
 */

export const FLOW_BRIEF_MAX = 1000;
export const FLOW_FACTS_MAX = 1000;
/** up to 4 minutes (choice ก 27 ต.ค. 2569 10:49): 30 shots — 4 min with 8-second shots, 3 min with 6, 2 min with 4 */
export const FLOW_MAX_SHOTS = 30;
export const FLOW_PROMPT_MAX = 3000;
export const FLOW_LANGS = ['en', 'th'] as const;
export type FlowLang = typeof FLOW_LANGS[number];
export const FLOW_ASPECTS = ['9:16', '16:9', '1:1'] as const;
export type FlowAspect = typeof FLOW_ASPECTS[number];
/** the length of one generated clip the person picks (set to what their Flow offers) */
export const FLOW_SHOT_SECONDS = [4, 6, 8] as const;
export const FLOW_TOTAL_MIN = 4, FLOW_TOTAL_MAX = 240;
/** how a shot is best made in Flow */
export const FLOW_MODES = ['text', 'ingredients', 'frames'] as const;
export type FlowMode = typeof FLOW_MODES[number];
export const FLOW_MODE_TH: Record<FlowMode, string> = { text: 'ข้อความอย่างเดียว', ingredients: 'Ingredients (แนบรูปอ้างอิง)', frames: 'Frames (ภาพแรก–ภาพสุดท้าย)' };

/*
 * ให้ Flow พากย์ด้วย (choices 27 ต.ค. 2569 09:16: ค สวิตช์ต่อชุด ปิดไว้ก่อน · กำหนดเสียงผู้บรรยายเอง). Veo makes sound with the
 * picture, speech included. When on, every shot's copied prompt ends with the Thai line, spoken by one narrator described
 * the same way in every shot (built here from the person's choices, not left to the AI, so it is the same word for word).
 * The format (a speaker, a colon and the words in quotes) follows the third-party guide read 27 ต.ค. (akool.com): without it
 * the words may be drawn as text. Each clip is made apart, so the same description does not promise the same voice — said
 * on the panel. The line also stays on the card, to voice it in CraftStudio instead.
 */
export const FLOW_VOICE_SEX = { male: { en: 'male', th: 'ชาย' }, female: { en: 'female', th: 'หญิง' } } as const;
export const FLOW_VOICE_AGE = { young: { en: 'young', th: 'วัยรุ่น' }, adult: { en: 'middle-aged', th: 'วัยทำงาน' }, senior: { en: 'elderly', th: 'สูงวัย' } } as const;
export const FLOW_VOICE_TONE = {
  warm: { en: 'warm and friendly', th: 'อบอุ่น เป็นกันเอง' }, bright: { en: 'bright and cheerful', th: 'สดใส ร่าเริง' },
  calm: { en: 'calm, documentary-style', th: 'นุ่ม สงบ แบบสารคดี' }, serious: { en: 'serious and trustworthy', th: 'จริงจัง น่าเชื่อถือ' },
  excited: { en: 'energetic and exciting', th: 'ตื่นเต้น มีพลัง' }
} as const;
export const FLOW_VOICE_EXTRA_MAX = 120;
export interface FlowVoice { sex: keyof typeof FLOW_VOICE_SEX; age: keyof typeof FLOW_VOICE_AGE; tone: keyof typeof FLOW_VOICE_TONE; extra: string }
/** voice = the narrator when Flow should speak the lines (ให้ Flow พากย์ด้วย), null = off */
export interface FlowRequest { brief: string; facts: string; lang: FlowLang; aspect: FlowAspect; total: number; shot: number; voice: FlowVoice | null }

/** the narrator, in the prompt language — the same words in every shot */
export function narratorText(v: FlowVoice, lang: FlowLang): string {
  const extra = v.extra ? (lang === 'th' ? ` ${v.extra}` : `, ${v.extra}`) : '';
  return lang === 'th'
    ? `ผู้บรรยาย${FLOW_VOICE_SEX[v.sex].th}${FLOW_VOICE_AGE[v.age].th} เสียง${FLOW_VOICE_TONE[v.tone].th} พูดภาษาไทยชัดเจน${extra}`
    : `a ${FLOW_VOICE_AGE[v.age].en} ${FLOW_VOICE_SEX[v.sex].en} Thai narrator with a ${FLOW_VOICE_TONE[v.tone].en} voice, speaking clear Thai${extra}`;
}
/** a narrator chosen on the panel (or stored with a request): known choices only, the extra words cleaned */
export function checkFlowVoice(v: unknown): Checked<FlowVoice | null> {
  if (v === undefined || v === null) return { ok: true, value: null };
  const o = v as Record<string, unknown>;
  if (typeof o !== 'object') return { ok: false, error: 'ข้อมูลเสียงผู้บรรยายไม่ถูกต้อง' };
  if (typeof o.sex !== 'string' || !(o.sex in FLOW_VOICE_SEX)) return { ok: false, error: 'เลือกเพศของเสียงผู้บรรยายก่อน' };
  if (typeof o.age !== 'string' || !(o.age in FLOW_VOICE_AGE)) return { ok: false, error: 'เลือกช่วงอายุของเสียงผู้บรรยายก่อน' };
  if (typeof o.tone !== 'string' || !(o.tone in FLOW_VOICE_TONE)) return { ok: false, error: 'เลือกโทนเสียงผู้บรรยายก่อน' };
  if (o.extra !== undefined && typeof o.extra !== 'string') return { ok: false, error: 'คำอธิบายเสียงเพิ่มเติมต้องเป็นข้อความ' };
  if (tooLong(o.extra, FLOW_VOICE_EXTRA_MAX)) return { ok: false, error: `คำอธิบายเสียงเพิ่มเติมยาวเกิน ${FLOW_VOICE_EXTRA_MAX} ตัวอักษร` };
  // quotes would break the "says: \"…\"" format: taken out
  const extra = clean(o.extra, FLOW_VOICE_EXTRA_MAX).replace(/["“”]/g, '');
  return { ok: true, value: { sex: o.sex as FlowVoice['sex'], age: o.age as FlowVoice['age'], tone: o.tone as FlowVoice['tone'], extra } };
}
export interface FlowShot { seconds: number; purpose: string; prompt: string; negative: string; mode: FlowMode; modeNote: string; onScreen: string; voice: string }
export interface FlowResult { title: string; style: string; shots: FlowShot[]; tips: string[]; notes: string[] }

type Checked<T> = { ok: true; value: T } | { ok: false; error: string };
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
/** a string cleaned of Windows line ends, at most `max` characters (counted as people see them) */
const clean = (v: unknown, max: number) => typeof v === 'string' ? [...v.normalize('NFC').replace(/\r\n?/g, '\n').trim()].slice(0, max).join('') : '';
const tooLong = (v: unknown, max: number) => typeof v === 'string' && [...v.normalize('NFC').trim()].length > max;

/** how many shots a request asks for */
export const flowShotCount = (total: number, shot: number) => Math.min(FLOW_MAX_SHOTS, Math.max(1, Math.ceil(total / shot)));

/** what the person asked for: words never cut (refused with the reason), choices from the lists only */
/** the total length against the shot length — one checker for the panel and the server (BUG-13); '' = fine */
export function flowLengthError(total: number, shot: number): string {
  if (!Number.isInteger(total) || total < FLOW_TOTAL_MIN || total > FLOW_TOTAL_MAX) return `ความยาวรวมต้องเป็น ${FLOW_TOTAL_MIN}–${FLOW_TOTAL_MAX} วินาที (จำนวนเต็ม)`;
  if (Math.ceil(total / shot) > FLOW_MAX_SHOTS) return `ช็อตละ ${shot} วินาที ยาวได้สุด ${FLOW_MAX_SHOTS * shot} วินาที (ไม่เกิน ${FLOW_MAX_SHOTS} ช็อต) ลดความยาวรวม หรือเพิ่มความยาวต่อช็อต`;
  return '';
}

export function checkFlowRequest(body: unknown): Checked<FlowRequest> {
  const b = (body ?? {}) as Record<string, unknown>;
  if (typeof b.brief !== 'string' || !b.brief.trim()) return { ok: false, error: 'ยังไม่ได้บอกว่าอยากได้วิดีโอแบบไหน พิมพ์ความต้องการก่อน' };
  if (tooLong(b.brief, FLOW_BRIEF_MAX)) return { ok: false, error: `ความต้องการยาวเกิน ${FLOW_BRIEF_MAX} ตัวอักษร (ตอนนี้ ${[...b.brief.trim()].length})` };
  if (b.facts !== undefined && b.facts !== null && typeof b.facts !== 'string') return { ok: false, error: 'ข้อเท็จจริงที่ต้องเห็นต้องเป็นข้อความ' };
  if (tooLong(b.facts, FLOW_FACTS_MAX)) return { ok: false, error: `ข้อเท็จจริงที่ต้องเห็นยาวเกิน ${FLOW_FACTS_MAX} ตัวอักษร` };
  if (!(FLOW_LANGS as readonly unknown[]).includes(b.lang)) return { ok: false, error: 'ภาษาของ prompt ต้องเป็นไทยหรืออังกฤษ' };
  if (!(FLOW_ASPECTS as readonly unknown[]).includes(b.aspect)) return { ok: false, error: 'สัดส่วนภาพต้องเป็น 9:16, 16:9 หรือ 1:1' };
  if (!(FLOW_SHOT_SECONDS as readonly unknown[]).includes(b.shot)) return { ok: false, error: `ความยาวต่อช็อตต้องเป็น ${FLOW_SHOT_SECONDS.join(', ')} วินาที` };
  const total = Number(b.total);
  const lengthError = flowLengthError(total, b.shot as number);
  if (lengthError) return { ok: false, error: lengthError };
  const voice = checkFlowVoice(b.voice);
  if (!voice.ok) return voice;
  return { ok: true, value: { brief: clean(b.brief, FLOW_BRIEF_MAX), facts: clean(b.facts, FLOW_FACTS_MAX), lang: b.lang as FlowLang, aspect: b.aspect as FlowAspect, total, shot: b.shot as number, voice: voice.value } };
}

/** the JSON schema sent to Gemini — kept simple (BUG-75: no long enums, no maxItems, required fields between optional ones) */
export function flowSchema() {
  return {
    type: 'object',
    properties: {
      title: { type: 'string', description: 'short Thai title of the video' },
      style: { type: 'string', description: 'the shared style block, in the prompt language' },
      shots: {
        type: 'array', minItems: 1,
        items: {
          type: 'object',
          properties: {
            purpose: { type: 'string', description: 'Thai: what this shot shows and why' },
            prompt: { type: 'string', description: 'the Flow prompt, in the prompt language' },
            negative: { type: 'string', description: 'what must not appear, in the prompt language' },
            mode: { type: 'string', enum: [...FLOW_MODES] },
            seconds: { type: 'number' },
            modeNote: { type: 'string', description: 'Thai: what to attach in Flow for this mode' },
            onScreen: { type: 'string', description: 'Thai text to put over this shot in CraftStudio' },
            voice: { type: 'string', description: 'Thai narration for this shot' }
          },
          required: ['purpose', 'prompt', 'negative', 'mode']
        }
      },
      tips: { type: 'array', items: { type: 'string' }, description: 'Thai tips for making this video in Flow' }
    },
    required: ['title', 'style', 'shots']
  };
}

/** the instructions sent with the person's words */
export function flowPrompt(r: FlowRequest): string {
  const n = flowShotCount(r.total, r.shot);
  const lang = r.lang === 'th' ? 'Thai' : 'English';
  return [
    'You are a film director and prompt writer for Google Flow (Veo video generation), working for a Thai agricultural and small-business group.',
    `Turn the request below into ${n} shots of about ${r.shot} seconds each (about ${r.total} seconds in all), aspect ratio ${r.aspect}.`,
    `Write "style" and every "prompt" and "negative" in ${lang}. Write "title", "purpose", "modeNote", "onScreen", "voice" and "tips" in Thai.`,
    '"style": one block shared by every shot so they look like one film — visual style (e.g. photorealistic macro, 3D cutaway animation), colour palette, lighting, lens and texture. Do not repeat it inside each prompt.',
    'Each "prompt": whole sentences (not a list of words) describing, in this order: the subject, what it does and how it moves, the setting and light, the camera (shot size, angle, movement), and the sound (ambient sound or effects; no spoken words unless the request asks for speech). It must make sense on its own with the style block in front of it.',
    'Each "negative": what must not appear — always include no text, no letters, no subtitles, no captions, no watermark, no logos; add what would be wrong for this shot.',
    '"mode": "text" = the prompt alone; "ingredients" = attach up to 3 reference images (say in modeNote which ones, e.g. a photo of the product or of the same durian tree) to keep the subject the same across shots; "frames" = a first and a last frame, for a precise change from one state to another. Use ingredients for a subject that must look the same in several shots.',
    'Words that people must read go in "onScreen" (short Thai, added later in CraftStudio, never drawn by Veo). Narration goes in "voice" (Thai, fits the shot length: about 4 Thai words per second at most).',
    ...(r.voice ? [
      `Veo will SPEAK each shot's "voice" line aloud (one narrator for the whole film: ${narratorText(r.voice, 'en')}). The line is added after your prompt by the app — do not write it inside "prompt".`,
      'So every shot needs a "voice" line short enough to be said calmly within the shot (leave about 1 second free), written as spoken Thai without quotation marks; in "prompt", describe only background sound and effects (no other speech, no music with lyrics).'
    ] : []),
    'Shots follow one story: an opening that catches the eye, the explanation in order, and an ending. Vary shot sizes and camera moves.',
    r.facts ? 'FACTS THAT MUST BE SHOWN CORRECTLY (follow them exactly; do not add claims that are not true or not given):\n' + r.facts : 'Do not invent technical or scientific claims; show only what the request says.',
    '"tips": 2–5 short Thai tips for making this video in Flow (e.g. which shot to make first, extending, keeping the subject the same).',
    '',
    'What the user wants:',
    r.brief
  ].join('\n');
}

/**
 * Gemini's answer made safe to show, for a request. Refused (with the reason): not an object, no shots, no usable shot.
 * Put right and said in notes: more shots than allowed (cut), a shot without a prompt (left out), an unknown mode
 * (text), a missing negative (the standard one), seconds outside the request (the requested length), a shot count
 * different from the one asked for.
 */
export function checkFlow(raw: unknown, r: FlowRequest): Checked<FlowResult> {
  if (!isObj(raw)) return { ok: false, error: 'AI ตอบกลับไม่ใช่ชุด prompt (ไม่ใช่ข้อมูลแบบที่ขอ)' };
  if (!Array.isArray(raw.shots) || !raw.shots.length) return { ok: false, error: 'AI ตอบกลับแต่ไม่มีช็อตเลย ลองเขียนความต้องการให้ชัดขึ้นแล้วสร้างใหม่' };
  const notes: string[] = [];
  const said = new Set<string>();
  const note = (t: string) => { if (!said.has(t)) { said.add(t); notes.push(t); } };
  const want = flowShotCount(r.total, r.shot);
  const std = r.lang === 'th' ? 'ห้ามมีตัวหนังสือ ตัวอักษร คำบรรยาย ซับไตเติล ลายน้ำ และโลโก้' : 'no text, no letters, no subtitles, no captions, no watermark, no logos';
  let list = raw.shots as unknown[];
  if (list.length > FLOW_MAX_SHOTS) { note(`AI แบ่งมา ${list.length} ช็อต เกิน ${FLOW_MAX_SHOTS} ใช้ ${FLOW_MAX_SHOTS} ช็อตแรก`); list = list.slice(0, FLOW_MAX_SHOTS); }
  const shots: FlowShot[] = [];
  let noPrompt = 0, fixedNeg = 0, fixedMode = 0, fixedSec = 0, cut = 0;
  for (const s of list) {
    if (!isObj(s) || typeof s.prompt !== 'string' || !s.prompt.trim()) { noPrompt++; continue; }
    if (tooLong(s.prompt, FLOW_PROMPT_MAX)) cut++;
    const mode = (FLOW_MODES as readonly unknown[]).includes(s.mode) ? s.mode as FlowMode : (fixedMode++, 'text');
    let negative = clean(s.negative, FLOW_PROMPT_MAX);
    if (!negative) { negative = std; fixedNeg++; }
    const sec = typeof s.seconds === 'number' && Number.isFinite(s.seconds) ? s.seconds : NaN;
    if (!(sec >= 1 && sec <= 30)) fixedSec += s.seconds === undefined ? 0 : 1;
    shots.push({
      seconds: sec >= 1 && sec <= 30 ? Math.round(sec) : r.shot,
      purpose: clean(s.purpose, 300) || `ช็อตที่ ${shots.length + 1}`,
      prompt: clean(s.prompt, FLOW_PROMPT_MAX), negative, mode,
      modeNote: clean(s.modeNote, 300), onScreen: clean(s.onScreen, 300), voice: clean(s.voice, 600)
    });
  }
  if (noPrompt) note(`มี ${noPrompt} ช็อตที่ AI ไม่ได้เขียน prompt มา จึงไม่ได้ใส่`);
  if (!shots.length) return { ok: false, error: 'AI ตอบกลับแต่ไม่มีช็อตที่ใช้ได้เลย (ไม่มี prompt) ลองสร้างใหม่' };
  if (cut) note(`prompt ${cut} ช็อตยาวเกิน ${FLOW_PROMPT_MAX} ตัวอักษร ตัดให้เหลือ ${FLOW_PROMPT_MAX} ตัว (แก้เองได้)`);
  if (fixedNeg) note(`AI ไม่ได้ใส่ข้อห้ามมา ${fixedNeg} ช็อต ใช้ข้อห้ามมาตรฐาน (ห้ามตัวหนังสือ ซับไตเติล ลายน้ำ โลโก้) แทน`);
  if (fixedMode) note(`AI ระบุวิธีสร้างไม่ถูก ${fixedMode} ช็อต ใช้ "ข้อความอย่างเดียว" แทน`);
  if (fixedSec) note(`ความยาวช็อตที่ AI ใส่มาไม่ถูก ${fixedSec} ช็อต ใช้ ${r.shot} วินาทีตามที่เลือก`);
  if (r.voice) {
    // ให้ Flow พากย์ด้วย: a shot without a line is silent in Flow — said, never left to be found out after paying Flow
    const silent = shots.map((s, i) => s.voice ? 0 : i + 1).filter(Boolean);
    if (silent.length) note(`ช็อตที่ ${silent.join(', ')} ไม่มีบทพากย์ จึงไม่มีเสียงพูดในช็อตนั้น (เพิ่มเองใน Flow หรือพากย์ใน CraftStudio ได้)`);
    const quoted = shots.filter(s => /["“”]/.test(s.voice)).length;
    if (quoted) note(`เอาเครื่องหมายคำพูดออกจากบทพากย์ ${quoted} ช็อต (ไม่งั้น Flow อาจอ่านผิดรูปแบบ)`);
  }
  if (shots.length !== want) note(`AI แบ่งมา ${shots.length} ช็อต (ขอ ${want} ช็อต)`);
  const style = clean(raw.style, FLOW_PROMPT_MAX);
  if (!style) note('AI ไม่ได้ส่งชุดสไตล์กลางมา ภาพแต่ละช็อตอาจไม่ไปทางเดียวกัน ลองสร้างใหม่ได้');
  const tips = Array.isArray(raw.tips) ? (raw.tips as unknown[]).map(t => clean(t, 300)).filter(Boolean).slice(0, 8) : [];
  return { ok: true, value: { title: clean(raw.title, 80) || 'วิดีโอจาก AI', style, shots, tips, notes } };
}

/** the text copied for one shot: the style block, the shot, the ratio and length, what must not appear */
export function flowCopyText(res: Pick<FlowResult, 'style'>, s: FlowShot, r: Pick<FlowRequest, 'lang' | 'aspect' | 'voice'>): string {
  const th = r.lang === 'th';
  // ให้ Flow พากย์ด้วย: the narrator and the Thai line, in the format Veo speaks (speaker, colon, the words in quotes)
  const line = s.voice.replace(/["“”]/g, '').trim();
  const speak = r.voice && line
    ? (th ? `เสียงบรรยาย (ไม่ปรากฏตัวในภาพ) โดย${narratorText(r.voice, 'th')} พูดว่า: "${line}"` : `Off-screen voice-over by ${narratorText(r.voice, 'en')}, who says: "${line}"`)
    : '';
  return [
    res.style,
    s.prompt,
    speak,
    th ? `สัดส่วนภาพ ${r.aspect} · ความยาวประมาณ ${s.seconds} วินาที` : `Aspect ratio ${r.aspect}. About ${s.seconds} seconds.`,
    (th ? 'ห้ามมี: ' : 'Negative: ') + s.negative
  ].filter(Boolean).join('\n\n');
}

/** the JSON Gemini wrote: its text, with a ```json fence taken off */
export function flowJsonOf(text: string): unknown {
  const t = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  try { return JSON.parse(t); } catch { return undefined; }
}
